/**
 * TraceX Type Definitions
 *
 * Types for Trace-specific data stored in glTF/glXF extras.
 * These are application-specific and not part of any glTF extension spec.
 */

import type { ManifestValidationOptions } from '../../../export/manifestValidator';
import type { BehaviorData } from '../../../scenedata/behaviordata';
import type { PropertySheet } from '../../../scenedata/behaviors/propertiesdata';
import type { ZipProgressCallback } from '../../../export/zip/zipWriter';
import type { ForeignDocumentData } from '../gltf21/foreign';

// =============================================================================
// Trace Extras (stored in glTF extras property)
// =============================================================================

/**
 * Document-level trace metadata, stored in the document's `extras.trace`.
 *
 * Tooling and registry data only — no scene semantics (SCENE_ROOT_SPEC §2.1).
 * What the file says about its CONTENT lives on the document root node.
 */
export interface TraceSceneExtras {
    /** Trace format version */
    version: string;
    /** Scene name */
    name?: string;
    /**
     * Save timestamp found in older documents. Never read, and not written:
     * a document is a function of its scene, so an unchanged scene saves to
     * the same bytes.
     */
    modified?: string;
    /**
     * Script `@type` → the path of the file that defines it (§2.1, D8). The
     * map names files the DOCUMENT carries, so it belongs to the document; a
     * pre-D8 document states it on `scenes[0].extras.trace` and the reader
     * lifts it (§3).
     */
    scripts?: Record<string, string>;
}

/**
 * The pre-D8 scene-entity payload, `scenes[n].extras.trace` — written
 * 2026-09-06 → 09-13 and **never written again** (SCENE_ROOT_SPEC D8).
 *
 * Read-only: the loader folds it onto the document root at the read boundary
 * (§3), which is the only place this type is used.
 */
export interface TraceSceneRootExtras {
    /** Behaviors that belonged to the scene root. */
    behaviors?: BehaviorData[];
    /** Script `@type` → archive path; lifted to the document's extras at read. */
    scripts?: Record<string, string>;
    /** Root active state; a binding string is authored data and round-trips. */
    active?: boolean | string;
}

/**
 * Node-level trace data stored in node.extras.trace
 */
export interface TraceNodeExtras {
    /**
     * Whether the node is active. Omitted when true (the default); written as
     * `false`, or as the BINDING that computes it (`"{visible}"`) — a binding
     * is authored data and MUST round-trip (SCENE_ROOT_SPEC §2.1).
     */
    active?: boolean | string;
    /** Array of behavior definitions attached to this node */
    behaviors?: BehaviorData[];
    /**
     * Index into the document's `files[]` — the carrier for the sources glTF
     * 2.1 forbids as external assets: images, video, audio, nested archives
     * (plan D3).
     */
    file?: number;
}

/**
 * Wrapper for accessing trace extras on any glTF object
 */
export interface TraceExtrasContainer {
    extras?: {
        trace?: TraceSceneExtras | TraceSceneRootExtras | TraceNodeExtras;
    };
}

// =============================================================================
// TraceX Archive Structure
// =============================================================================

/**
 * Contents of a bundle archive (`.glz`, or a legacy `.tracex`)
 */
export interface TraceXArchive {
    /** The scene document (`scene.gltf`, or a legacy `scene.glxf`) as text. */
    sceneGlxf: string;
    /** Map of asset paths to their data */
    assets: Map<string, Uint8Array>;
}

// =============================================================================
// Import/Export Options
// =============================================================================

/**
 * The resolver's answer for a file that **already exists in the destination
 * folder** — a folder file the scene loaded over the network and never
 * changed (spec §2.1–§2.3).
 *
 * Such a file is a legitimate manifest entry with nothing to carry: the folder
 * holds the bytes, the document names them relatively, and a save is one JSON
 * write (spec §2.2). Distinct from `null`, which means *unresolvable* — the
 * difference between "already there" and "gone" is the whole of bug C: they
 * used to be the same answer, and the writer dropped both.
 */
export const ASSET_ALREADY_IN_FOLDER = 'already-in-folder' as const;

/** What a {@link TraceXExportOptions.assetResolver} may answer. */
export type AssetResolution = Uint8Array | typeof ASSET_ALREADY_IN_FOLDER | null;

/**
 * Options for importing TraceX files
 */
export interface TraceXImportOptions {
    /** Whether to validate the glXF structure */
    validate?: boolean;
}

/**
 * Options for exporting TraceX files
 */
export interface TraceXExportOptions {
    /** Export-local snapshots of loaded sheets, keyed by their prepared reference. */
    propertySheets?: ReadonlyMap<string, PropertySheet>;
    /** The document's name when it has no single root; otherwise the root names it. */
    sceneName?: string;
    /** Trace format version */
    traceVersion?: string;
    /** Whether to include trace extras (behaviors, etc.) */
    includeTraceExtras?: boolean;
    /**
     * Write the document root's BehaviorGraph as ratified `KHR_interactivity`
     * (see {@link KHR_INTERACTIVITY_EXPORT_DEFAULT}). Off, the graph stays in
     * the root's `extras.trace.behaviors`, the form every Trace client reads.
     */
    khrInteractivity?: boolean;
    /**
     * Asset resolver — the bytes for a referenced path, or one of the two
     * non-byte answers.
     *
     * `ASSET_ALREADY_IN_FOLDER` says: this is a folder file the DESTINATION
     * folder already holds (spec §2.2 — "nothing re-uploads a binary the
     * folder already holds"). It is a valid manifest entry that travels with
     * no bytes. `null` says the reference could not be resolved at all.
     */
    assetResolver?: (path: string) => Promise<AssetResolution>;
    /** A compatibility bake changes these bytes; even a hosted reference must be resolved. */
    assetRequiresBake?: (path: string) => boolean;
    /** Context for classifying external references; same policy as the folder preflight. */
    manifest?: ManifestValidationOptions;
    /** Whether to include asset files in the archive */
    includeAssets?: boolean;
    /**
     * What to do when a relative `assets/…` file or a node's owned source
     * resolves to nothing — no bytes, permitted live reference or folder
     * mapping. Origin-served site-absolute prototypes retain their drop policy.
     *
     * `'drop'` (default) forgets the entry and records a warning: the archive
     * writers' long-standing behaviour (tv#717), and the right one for a
     * DOWNLOAD of a legacy scene whose bytes really are gone — a partial
     * archive beats no archive, and the manifest must not promise a file the
     * ZIP does not contain.
     *
     * `'throw'` refuses. That is the right one for a SAVE: relative files and
     * owned node sources must survive, and writing without them is not a
     * partial export, it is a save that deletes the model. `SceneExporter`'s
     * folder path passes it.
     */
    unresolvedLocalAssets?: 'throw' | 'drop';
    /**
     * Emit `files[k].uri` as the original `src` value rather than rewriting it
     * to `assets/<filename>`. Use when producing a standalone `.gltf` document
     * whose files are served from external URLs (no bundled `assets/` folder).
     *
     * Caller is responsible for ensuring every reference is a usable absolute
     * URL — see `validateSrcRefAssets` for the precondition check.
     */
    preserveExternalUris?: boolean;
    /** Script `@type` → archive path, written to the document's `extras.trace.scripts`. */
    scripts?: Record<string, string>;
    /**
     * The ORIGINAL human name of a referenced file, for `files[k].name`
     * (spec §2.1, §3.6) — `chair.glb` for a blob stored as
     * `assets/3f9a…c21e.glb`.
     *
     * Informational: no reader resolves by it, so a lookup that answers
     * `undefined` simply omits the field. `SceneExporter` wires this to
     * `ResourceManager.getAssetName`, which holds (in order of authority) the
     * `name` echoed by `POST /files`, the dropped `File.name` recorded at
     * import, and the `files[k].name` of the document that was loaded.
     */
    assetName?: (path: string) => string | undefined;
    /** Fires after each archive entry is written during ZIP assembly. */
    onProgress?: ZipProgressCallback;
    /**
     * The document-level payload the OPENED document carried and this writer
     * did not author: `extras` keys beside `trace`, `extensions` other than the
     * ones generated here, `extensionsUsed` / `extensionsRequired`, and
     * `asset.extras` (issue #834, spec §3.6).
     *
     * Merged back with the Trace payload written last, so a save is a readable
     * diff of the previous one. Passed only when the output IS that document —
     * a subtree export (promotion) is a new document with its own provenance,
     * and takes none of it.
     */
    foreignDocument?: ForeignDocumentData;
}
